import React from 'react';
import { Box } from '@mui/material';
import { BrandExpandCardsDivider, BRAND_CARD_GRID_INITIAL_COUNT } from './BrandExpandCardsDivider';
import { BrandCampaignsListPagination } from './BrandCampaignsListPagination';

export interface BrandCampaignsPaginatedListProps<T> {
    items: T[];
    renderItem: (item: T, index: number) => React.ReactNode;
    getItemKey: (item: T, index: number) => string;
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    sectionLabel: string;
    expanded: boolean;
    onToggleExpanded: () => void;
}

/**
 * Shows the first {@link BRAND_CARD_GRID_INITIAL_COUNT} rows, optional expand for the rest of the **current page**,
 * then pagination (each page replaces the list — nothing is concatenated across pages).
 */
export function BrandCampaignsPaginatedList<T>({
    items,
    renderItem,
    getItemKey,
    page,
    totalPages,
    onPageChange,
    sectionLabel,
    expanded,
    onToggleExpanded,
}: BrandCampaignsPaginatedListProps<T>) {
    const initialItems = items.slice(0, BRAND_CARD_GRID_INITIAL_COUNT);
    const restItems = items.slice(BRAND_CARD_GRID_INITIAL_COUNT);
    const hasMoreOnPage = items.length > BRAND_CARD_GRID_INITIAL_COUNT;

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {initialItems.map((item, index) => (
                    <React.Fragment key={getItemKey(item, index)}>{renderItem(item, index)}</React.Fragment>
                ))}
                {hasMoreOnPage && (
                    <BrandExpandCardsDivider
                        expanded={expanded}
                        onToggle={onToggleExpanded}
                        sectionLabel={sectionLabel}
                    />
                )}
                {expanded &&
                    restItems.map((item, index) => (
                        <React.Fragment key={getItemKey(item, index + BRAND_CARD_GRID_INITIAL_COUNT)}>
                            {renderItem(item, index + BRAND_CARD_GRID_INITIAL_COUNT)}
                        </React.Fragment>
                    ))}
            </Box>
            <BrandCampaignsListPagination page={page} totalPages={totalPages} onChange={onPageChange} />
        </>
    );
}
